import { StatusBadge } from 'components/Badges/StatusBadge';
import { ComponentProps, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UNHEALTHY_STATUSES } from 'utils/siteStatus';
import * as Icons from '../Badges/icons';
import { SiteStatus } from 'types/Site';
import { SitesStatusTooltip } from 'components/Tooltips/SitesStatusTooltip';

interface SiteStatusBadgeProps extends ComponentProps<'svg'> {
    status: SiteStatus;
    iconOnly?: boolean;
    tooltip?: boolean;
}

export const SiteStatusBadge = ({
    status,
    iconOnly,
    tooltip,
    ...props
}: SiteStatusBadgeProps) => {
    const { t } = useTranslation('common');

    if (UNHEALTHY_STATUSES.includes(status)) {
        // group unhealthy statuses as one
        status = 'unhealthy';
    }

    const [Icon, color] = useMemo(() => {
        switch (status) {
            case 'healthy':
                return [Icons.CircleCheck, 'green'];
            case 'unhealthy':
                return [Icons.CircleExclamation, 'yellow'];
            case 'down':
                return [Icons.CircleX, 'red'];
            case 'provisioning':
                return [Icons.CircleDots, 'purple'];
            case 'pending':
                return [Icons.CircleHourglass, 'blue'];
            default:
                return [() => <span />, 'grey'];
        }
    }, [status]);

    const component = iconOnly ? (
        <Icon {...props} />
    ) : (
        <StatusBadge color={color} Icon={Icon} {...props}>
            {t(`site.status.${status}`, status)}
        </StatusBadge>
    );

    if (tooltip) {
        return (
            <SitesStatusTooltip status={status} icon={<Icon />}>
                {component}
            </SitesStatusTooltip>
        );
    }

    return component;
};
